import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useBilling } from '../../hooks/useBilling';
import { CheckCircle, Crown, Zap, Users, Database, BarChart3, Shield, Headphones, Star, Check } from 'lucide-react';
import { toast } from 'sonner';

interface PlanComparisonProps {
  currentPlan: string;
}

export function PlanComparison({ currentPlan }: PlanComparisonProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const { useProducts, createCheckoutSession, isCreatingCheckout } = useBilling();
  const { data: productsData, isLoading } = useProducts();

  const handleUpgrade = async (productId: string, productName: string) => {
    try {
      await createCheckoutSession({
        productId,
        successUrl: `${window.location.origin}/billing?success=true`,
        cancelUrl: `${window.location.origin}/billing?canceled=true`
      });
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(`Failed to start checkout for ${productName}`);
    }
  };

  // Use real products data or fallback to mock data
  const products = productsData?.products || [];
  
  // Filter products by billing cycle
  const filteredProducts = products.filter((product: any) => 
    product.billing_interval === (billingCycle === 'monthly' ? 'month' : 'year')
  );

  // If no products match the billing cycle, show all products
  const displayProducts = filteredProducts.length > 0 ? filteredProducts : products;

  const getPrice = (product: any) => {
    return product.price_cents / 100;
  };

  const getSavings = (product: any) => {
    // Find corresponding monthly/yearly product for savings calculation
    const otherInterval = product.billing_interval === 'month' ? 'year' : 'month';
    const otherProduct = products.find((p: any) => 
      p.name.includes(product.name.replace(' Annual', '')) && p.billing_interval === otherInterval
    );
    
    if (!otherProduct) return 0;
    
    if (product.billing_interval === 'year') {
      const monthlyTotal = (otherProduct.price_cents / 100) * 12;
      const yearlyPrice = product.price_cents / 100;
      const savings = monthlyTotal - yearlyPrice;
      return Math.round((savings / monthlyTotal) * 100);
    }
    
    return 0;
  };

  const isCurrentPlan = (productName: string) => {
    return productName.toLowerCase().includes(currentPlan.toLowerCase().replace(' plan', ''));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
            <Star className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Available Plans</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">Choose the plan that fits your needs</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" className="text-sm">
            Annual (Save 20%)
          </Button>
          <Button variant="outline" className="text-sm">
            Monthly
          </Button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {displayProducts.map((product: any, index: number) => (
          <Card key={index} className={`p-6 relative ${
            product.is_popular ? 'border-2 border-purple-500 dark:border-purple-400' : ''
          }`}>
            {product.is_popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-purple-600 text-white">Most Popular</Badge>
              </div>
            )}

            {isCurrentPlan(product.name) && (
              <div className="absolute -top-3 right-4">
                <Badge variant="default" className="px-3 py-1">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Current Plan
                </Badge>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{product.name}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{product.description}</p>
              <div className="flex items-baseline justify-center">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">${getPrice(product)}</span>
                <span className="text-gray-600 dark:text-gray-400 ml-2">/{product.billing_interval === 'month' ? 'mo' : 'yr'}</span>
                {product.billing_interval === 'year' && getSavings(product) > 0 && (
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium mt-1">
                    Save {getSavings(product)}% annually
                  </div>
                )}
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {product.product_features?.map((feature: any, featureIndex: number) => (
                <li key={featureIndex} className="flex items-center">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">{feature.feature_name}</span>
                  {feature.feature_type === 'limit' && feature.feature_value && (
                    <span className="text-gray-500 dark:text-gray-500 ml-1">({feature.feature_value})</span>
                  )}
                </li>
              )) || (
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">Full access to all features</span>
                </li>
              )}
            </ul>

            <Button 
              className={`w-full ${
                product.is_popular 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                  : isCurrentPlan(product.name)
                  ? 'bg-gray-600 hover:bg-gray-700 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              variant={product.is_popular ? 'default' : isCurrentPlan(product.name) ? 'secondary' : 'outline'}
              disabled={isCurrentPlan(product.name) || isCreatingCheckout}
              onClick={() => !isCurrentPlan(product.name) && handleUpgrade(product.id, product.name)}
            >
              {isCurrentPlan(product.name) ? 'Current Plan' : `Upgrade to ${product.name}`}
            </Button>
          </Card>
        ))}
      </div>

      {/* Usage Limits */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Usage Limits:</h3>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center">
            <Database className="w-3 h-3 text-gray-500 dark:text-gray-400 mr-1" />
            <span className="text-gray-600 dark:text-gray-300">
              {displayProducts[0]?.max_projects || 'Unlimited'} projects
            </span>
          </div>
          <div className="flex items-center">
            <Users className="w-3 h-3 text-gray-500 dark:text-gray-400 mr-1" />
            <span className="text-gray-600 dark:text-gray-300">
              {displayProducts[0]?.max_team_members || 'Unlimited'} members
            </span>
          </div>
          <div className="flex items-center">
            <Zap className="w-3 h-3 text-gray-500 dark:text-gray-400 mr-1" />
            <span className="text-gray-600 dark:text-gray-300">{displayProducts[0]?.max_ai_requests?.toLocaleString() || 'Unlimited'} AI requests</span>
          </div>
        </div>
      </Card>

      {/* Feature Comparison Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Feature Comparison</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Features</th>
                {displayProducts.map((product: any, index: number) => (
                  <th key={index} className="text-center py-3 px-4 font-medium text-gray-900 dark:text-white">
                    {product.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[
                { name: 'AI Agents', values: ['Analytics Only', 'All 5 Agents', 'All + Custom'] },
                { name: 'Projects', values: ['1', '4', 'Unlimited'] },
                { name: 'Team Members', values: ['2', '5', 'Unlimited'] },
                { name: 'API Calls/month', values: ['1,000', '2,000', '10,000'] },
                { name: 'Data Retention', values: ['30 days', '2 years', 'Unlimited'] },
                { name: 'Support', values: ['Email', 'Priority', 'Dedicated'] },
                { name: 'API Access', values: ['❌', '✅', '✅'] },
                { name: 'White-label', values: ['❌', '❌', '✅'] },
                { name: 'SLA', values: ['❌', '❌', '✅'] }
              ].map((feature, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                    {feature.name}
                  </td>
                  {feature.values.map((value, valueIndex) => (
                    <td key={valueIndex} className="py-3 px-4 text-sm text-center text-gray-700 dark:text-gray-300">
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Enterprise Contact */}
      <Card className="p-8 bg-gradient-to-r from-gray-50 dark:from-gray-800 to-blue-50 dark:to-blue-900/20 border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Need something more?</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Contact our sales team for custom enterprise solutions with advanced features, 
              dedicated support, and custom integrations.
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                Custom data retention
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                Dedicated account manager
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                Priority support
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                Custom integrations
              </li>
            </ul>
          </div>
          <div className="ml-8">
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              Contact Sales
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
} 